import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { SBALogo } from "@/components/SBALogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AskAboutCard } from "@/components/AskAboutCard";
import { SbaSteps, AdvisorHelpSection, TpmoDisclosure, ComparePreview } from "@/components/sba/SbaShared";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ShieldCheck, PhoneCall, ArrowRight, Loader2, MessageSquare,
} from "lucide-react";

// SBA public line = Nick's verified, configured SBA business number (Telnyx mapping, live routing).
const SBA_PHONE = { tel: "+13866990068", label: "(386) 699-0068" };

const sbaVisitId = () => {
  try {
    let v = sessionStorage.getItem("sba_visit");
    if (!v) { v = Array.from(crypto.getRandomValues(new Uint8Array(12)), (b) => b.toString(16).padStart(2, "0")).join(""); sessionStorage.setItem("sba_visit", v); }
    return v;
  } catch { return ""; }
};

export default function SeniorBenefitAdvisors() {
  const navigate = useNavigate();
  const [resumeOpen, setResumeOpen] = useState(false);
  const [brand, setBrand] = useState(null);
  const [smsReady, setSmsReady] = useState(false);

  useEffect(() => {
    api.get("/public/brand/sba").then((r) => setBrand(r.data)).catch(() => {});
    api.get("/f/_agency/sba").then((r) => setSmsReady(!!r.data?.sms_ready && r.data?.business_phone === SBA_PHONE.tel)).catch(() => setSmsReady(false));
    // Anonymous funnel landing (allowlisted UTM identifiers only; deduped per visit across refreshes).
    const sp = new URLSearchParams(window.location.search);
    const body = { visit_id: sbaVisitId(), qa: sp.get("qa") === "1" };
    ["src", "utm_source", "utm_medium", "utm_campaign", "utm_content"].forEach((k) => { if (sp.get(k)) body[k] = sp.get(k).slice(0, 100); });
    api.post("/public/funnel/landing", body).catch(() => {});
  }, []);

  // SBA front door -> the shared Ask review/compare flow, keeping SBA source + ad UTM params.
  // Legacy assessment routes stay only for saved resume links.
  const start = (help) => {
    const params = new URLSearchParams(window.location.search);
    // Agency entry precedence: /sba is ALWAYS the general agency journey (unassigned for manual
    // distribution) — never carry a personal advisor ref from this page.
    params.delete("ref");
    params.set("src", "sba");
    params.set("v", sbaVisitId());
    if (help === true) params.set("help", "advisor");
    navigate(`/ask?${params.toString()}`);
  };

  return (
    <div className="min-h-screen bg-[#FBFBF9] text-[#111827]">
      {/* Nav */}
      <header className="sticky top-0 z-30 bg-[#FBFBF9]/90 backdrop-blur border-b border-[#E5E5E0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 h-20 flex items-center justify-between">
          <div className="flex flex-col justify-center leading-none shrink-0" data-testid="sba-brand-lockup">
            <SBALogo className="h-8 sm:h-10" />
            <span data-testid="sba-brand-tagline" className="mt-2 pl-[2px] text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.24em] text-[#1E3E62] hidden sm:block">
              Senior Intelligence. Better Decisions.
            </span>
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            <a href={`tel:${SBA_PHONE.tel}`} className="hidden sm:inline-flex items-center gap-2 text-[#1E3E62] font-semibold" data-testid="nav-sba-phone" aria-label={`Call ${SBA_PHONE.label}`}>
              <PhoneCall className="w-4 h-4 text-[#2D6A4F]" /><span className="hidden sm:inline select-all">{SBA_PHONE.label}</span>
            </a>
            {smsReady && (
              <a href={`sms:${SBA_PHONE.tel}`} className="hidden sm:inline-flex items-center gap-1 text-[#1E3E62] font-semibold whitespace-nowrap" data-testid="nav-sba-text" aria-label={`Text SBA at ${SBA_PHONE.label}`}>
                <MessageSquare className="w-4 h-4 text-[#2D6A4F]" /><span>Text SBA</span>
              </a>
            )}
            <Button variant="ghost" className="hidden md:inline-flex h-11 text-base text-[#1E3E62] hover:bg-[#F0F7F4]" onClick={() => setResumeOpen(true)} data-testid="nav-resume-session-button">
              Continue your visit
            </Button>
            <Link to="/login?brand=sba">
              <Button variant="outline" className="h-11 px-3 sm:px-4 text-sm sm:text-base whitespace-nowrap rounded-full border-[#BCCCDC] text-[#0B192C]" data-testid="nav-member-signin-link">
                Member sign in
              </Button>
            </Link>
          </div>
        </div>
        <div className="sm:hidden border-t border-[#E5E5E0] px-4 py-2 flex gap-2" data-testid="nav-mobile-contact-row">
          <a href={`tel:${SBA_PHONE.tel}`} className="flex-1 h-11 inline-flex items-center justify-center gap-2 rounded-full border border-[#BCCCDC] bg-white text-[#1E3E62] font-semibold whitespace-nowrap" data-testid="nav-mobile-sba-call" aria-label={`Call SBA at ${SBA_PHONE.label}`}>
            <PhoneCall className="w-4 h-4 text-[#2D6A4F]" />Call SBA
          </a>
          {smsReady && (
            <a href={`sms:${SBA_PHONE.tel}`} className="flex-1 h-11 inline-flex items-center justify-center gap-2 rounded-full border border-[#BCCCDC] bg-white text-[#1E3E62] font-semibold whitespace-nowrap" data-testid="nav-mobile-sba-text" aria-label={`Text SBA at ${SBA_PHONE.label}`}>
              <MessageSquare className="w-4 h-4 text-[#2D6A4F]" />Text SBA
            </a>
          )}
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 sm:px-8 pt-10 pb-4 sm:pt-14 grid lg:grid-cols-[1.15fr_1fr] gap-8 lg:gap-12 items-start">
        <div className="animate-rise">
          <span className="inline-flex items-center gap-2 text-[#2D6A4F] text-sm font-bold tracking-wide uppercase mb-4">
            <ShieldCheck className="w-4 h-4" /> Medicare questions, answered clearly
          </span>
          <h1 className="font-display font-bold text-4xl sm:text-5xl tracking-tight leading-[1.1] text-[#0B192C]" data-testid="hero-headline">
            Ask your Medicare questions and explore plan options.
          </h1>
          <p className="mt-5 text-lg leading-relaxed text-[#374151] max-w-xl">
            Use Ask Nauticus to get plain-language answers and compare plan facts for your county. A licensed insurance
            advisor from Senior Benefit Advisors is available whenever you want human help, at no cost for the assistance.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3">
            <Button size="lg" onClick={() => start()} className="h-14 px-8 text-lg font-bold rounded-full bg-[#D4AF37] text-[#0B192C] hover:bg-[#B89620]" data-testid="hero-ask-cta">
              Ask Nauticus <ArrowRight className="w-5 h-5 ml-1" />
            </Button>
            <Button size="lg" variant="outline" onClick={() => start(true)} className="h-14 px-6 text-base font-bold rounded-full border-2 border-[#0B192C] text-[#0B192C]" data-testid="hero-advisor-cta">
              Get help from a licensed advisor
            </Button>
          </div>
          <p className="mt-3 text-sm text-[#4B5563]">Private by default — we only save information if you ask us to.</p>
        </div>
        <ComparePreview />
      </section>

      <SbaSteps />
      <AdvisorHelpSection onAdvisor={() => start(true)} who="a licensed insurance advisor from Senior Benefit Advisors" />
      <div className="max-w-6xl mx-auto px-4 sm:px-8 pb-10"><AskAboutCard testid="sba-ask-about" className="text-sm" /></div>

      {/* Compliance footer */}
      <footer className="bg-[#0B192C] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-10">
          <div className="flex flex-col sm:flex-row justify-between gap-8">
            <div className="max-w-sm">
              <SBALogo light />
              <p className="mt-4 text-base text-[#BCCCDC] leading-relaxed">
                Senior Benefit Advisors is a licensed insurance agency helping people review Medicare and insurance options.
              </p>
            </div>
            <div className="flex gap-10 text-sm">
              <div>
                <p className="font-bold text-white mb-3">Company</p>
                <ul className="space-y-2 text-[#BCCCDC]">
                  <li><Link to="/login?brand=sba" className="hover:text-[#D4AF37]">Member sign in</Link></li>
                  <li><button onClick={() => setResumeOpen(true)} className="hover:text-[#D4AF37]">Continue your visit</button></li>
                </ul>
              </div>
            </div>
          </div>
          <div className="mt-8 pt-6 border-t border-white/10 text-base text-[#DCE4EC] leading-relaxed space-y-3">
            {(brand?.licensing_disclosures || []).filter((d) => !(d.text || "").startsWith(brand?.legal_entity_name || "\u0000")).map((d, i) => (
              <p key={i} data-testid={`footer-disclosure-${i}`}>{d.text || `${d.state}: ${d.type}`}</p>
            ))}
            <p>
              This is a solicitation for insurance. By submitting a request you consent to be contacted about
              Medicare and insurance options. Consent is not a condition of purchase. Plan availability varies by location.
            </p>
            <TpmoDisclosure dark entity={brand?.legal_entity_name || "Senior Benefit Advisors of Florida, LLC"} tpmo={brand?.tpmo_disclaimer} testid="footer-tpmo" />
          </div>
        </div>
      </footer>

      <ResumeModal open={resumeOpen} onOpenChange={setResumeOpen} />
    </div>
  );
}

function ResumeModal({ open, onOpenChange }) {
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);

  const lookup = async () => {
    setBusy(true);
    try {
      // Canonical resume lookup: returns an `assessment` field for new SBA
      // assessment tokens (null for legacy intake tokens). Works for both.
      const res = await api.post("/public/assessment/resume", { resume_token: token.trim() });
      setData(res.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setData(null);
    } finally {
      setBusy(false);
    }
  };

  const close = (o) => { if (!o) { setToken(""); setData(null); } onOpenChange(o); };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-md bg-[#FBFBF9]" data-testid="resume-session-modal">
        <DialogHeader><DialogTitle className="font-display text-2xl text-[#0B192C]">Continue your visit</DialogTitle></DialogHeader>
        <p className="text-base text-[#4B5563]">Have a resume code from an earlier visit? Enter it to pick up where you left off. If you saved to My Nauticus with an account, use Member sign in instead.</p>
        <Input value={token} onChange={(e) => setToken(e.target.value)} placeholder="Resume code" className="h-12 text-base bg-white mt-2" data-testid="resume-modal-token-input" />
        <Button disabled={!token.trim() || busy} onClick={lookup} className="h-12 text-base font-bold rounded-full bg-[#0B192C] text-white hover:bg-[#1E3E62]" data-testid="resume-modal-submit-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Find my request"}
        </Button>
        {data && (
          <div className="mt-2 rounded-xl bg-white border border-[#E5E5E0] p-4" data-testid="resume-result">
            <p className="font-display font-bold text-lg text-[#0B192C]">Welcome back{data.person?.first_name ? `, ${data.person.first_name}` : ""}!</p>
            <p className="text-sm text-[#4B5563] mt-1">Status: <span className="font-semibold capitalize">{data.household?.status}</span> · {data.journeys?.length || 0} request(s) on file.</p>
            {data.assessment ? (
              data.assessment.status === "completed" ? (
                <Link to={`/sba/assessment/results?resume=${encodeURIComponent(token.trim())}`}>
                  <Button className="w-full mt-4 h-12 text-base font-bold rounded-full bg-[#D4AF37] text-[#0B192C] hover:bg-[#B89620]" data-testid="resume-view-results-btn">
                    View my results
                  </Button>
                </Link>
              ) : (
                <Link to={`/sba/assessment?resume=${encodeURIComponent(token.trim())}`}>
                  <Button className="w-full mt-4 h-12 text-base font-bold rounded-full bg-[#D4AF37] text-[#0B192C] hover:bg-[#B89620]" data-testid="resume-continue-assessment-btn">
                    Continue my assessment
                  </Button>
                </Link>
              )
            ) : (
              !data.household?.claimed && (
                <Link to={`/register?resume=${encodeURIComponent(token.trim())}`}>
                  <Button className="w-full mt-4 h-12 text-base font-bold rounded-full bg-[#D4AF37] text-[#0B192C] hover:bg-[#B89620]" data-testid="resume-create-account-btn">
                    Create account to save progress
                  </Button>
                </Link>
              )
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
